import axios from "axios";
import { baseUrl } from "./urls";
import {
  fetchEncryptionKey,
  encryptRequestData,
  decryptResponseData,
  clearEncryptionKey,
} from "./encryption";
import {
  storeTokens,
  clearSession,
  setLogoutReason,
  ACTIVITY_EVENT,
} from "./session";

const ENCRYPTED_HEADER = "X-Encrypted";
const EXCLUDED_ENCRYPTION_PATHS = [
  "/api/auth/otp/",
  "/api/auth/password-reset/",
  "/api/token/",
  "/api/encryption-key/",
  "/api/refresh-cookies/",
];

const shouldEncrypt = (url) => {
  if (!url) return false;
  return !EXCLUDED_ENCRYPTION_PATHS.some((path) => url.includes(path));
};

const instance = axios.create({
  baseURL: baseUrl,
});

function redirectToLogin() {
  const loginUrl = `/login`;
  window.history.pushState(null, '', loginUrl);
  const popStateEvent = new PopStateEvent('popstate', { state: null });
  dispatchEvent(popStateEvent);
}

// Hard logout: record why (so the login screen can explain it), clear all
// client-side session state, and redirect to /login. `code` is the value from a
// 401 response (token_revoked | session_idle) or null for an ordinary expiry.
function hardLogout(code) {
  setLogoutReason(code);
  clearEncryptionKey();
  clearSession();
  redirectToLogin();
}

// Request interceptor
instance.interceptors.request.use(
    function (config) {
    if (
      config.url?.includes('/api/auth/otp/') ||
      config.url?.includes('/api/auth/password-reset/') ||
      config.url?.includes('/api/token/')
    ) {
      return config;
    }

    let access = null;
    try {
      const tokensJson = localStorage?.getItem('authTokens');
      const tokens = JSON.parse(tokensJson);
      access = tokens?.access;
    } catch (error) {
      console.error('Error parsing auth tokens:', error);
    }

    if (!access) {
      redirectToLogin();
      return Promise.reject(new Error('No access token'));
    }

    config.headers.Authorization = `Bearer ${access}`;
    return config;
  },
  function (error) {
    console.error('Axios request interceptor error:', error);
    return Promise.reject(error);
  }
);

instance.interceptors.request.use(
  async function (config) {

    if (shouldRefreshCloudfrontCookies()) {
      await refreshCloudfrontCookies();
    }

    if (shouldEncrypt(config.url) && !(config.data instanceof FormData) && !process.env.REACT_APP_LOCAL_APP) {
      try {
        const keyHex = await fetchEncryptionKey();

        if (config.data) {
          const encrypted = encryptRequestData(config.data, keyHex);
          config.data = encrypted;
        }
        // if(process.env.REACT_APP_LOCAL_APP){
        //   config.headers[ENCRYPTED_HEADER] = "false";
        // }else{
        config.headers[ENCRYPTED_HEADER] = "true";
        // }
      } catch (err) {
        console.error("Encryption error in request interceptor:", err);
      }
    }

    return config;
  }
);

// Response interceptor
instance.interceptors.response.use(
  async function (response) {
    if (
      response.data &&
      response.data.iv &&
      response.data.ciphertext &&
      shouldEncrypt(response.config.url)
    ) {
      try {
        const keyHex = await fetchEncryptionKey();
        const decrypted = decryptResponseData(
          response.data.iv,
          response.data.ciphertext,
          keyHex
        );
        response.data = decrypted;
      } catch (err) {
        console.error("Decryption error in response interceptor:", err);
      }
    }

    // Signal real API traffic to the idle manager: a successful authenticated
    // response means the server's idle window just slid.
    if (shouldEncrypt(response.config?.url)) {
      window.dispatchEvent(new Event(ACTIVITY_EVENT));
    }

    return response;
  },
  async function (error) {

    const originalRequest = error.config;

    // Bail on the token endpoints (login/verify/refresh): a 401 there is a
    // credential/refresh failure the caller handles, not something to refresh or
    // hard-logout on — and it prevents recursing on the refresh call itself.
    if (!originalRequest || originalRequest.url?.includes('/api/token/')) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const code = error.response?.data?.code;

    // 408 => fatal, hard logout (unchanged behavior).
    if (status === 408) {
      hardLogout(null);
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    // Revocation / idle can NOT be refreshed away: a revoked refresh token fails
    // anyway, and the refresh endpoint doesn't enforce idle (it would re-seed
    // activity and silently defeat the timeout). Log out with the right message.
    if (code === 'token_revoked' || code === 'session_idle') {
      hardLogout(code);
      return Promise.reject(error);
    }

    // Loop guard: a replayed request that 401s again gets a clean logout.
    if (originalRequest._retry) {
      hardLogout(null);
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    try {
      const access = await getRefreshedAccessToken();
      originalRequest.headers.Authorization = `Bearer ${access}`;
      return instance(originalRequest);
    } catch (err) {
      hardLogout(err?.code || null);
      return Promise.reject(error);
    }
  }
);

// Single-flight token refresh: concurrent 401s all await the same in-flight
// refresh instead of each firing their own (no refresh storm). Modeled on
// refreshCloudfrontCookies below. Resolves the new access token or throws
// { code } carrying the 401 code from a failed refresh.
let refreshPromise = null;

const makeRefreshError = (code) => {
  const err = new Error('Token refresh failed');
  err.code = code;
  return err;
};

const getRefreshedAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let refresh = null;
    try {
      refresh = JSON.parse(localStorage.getItem('authTokens'))?.refresh || null;
    } catch (err) {
      refresh = null;
    }
    if (!refresh) {
      throw makeRefreshError(null);
    }

    let response;
    try {
      // Raw axios (not the instance) to skip interceptors / encryption.
      response = await axios.post(`${baseUrl}/api/token/refresh/`, { refresh });
    } catch (err) {
      throw makeRefreshError(err.response?.data?.code || null);
    }

    const data = response.data;
    if (!data?.access) {
      throw makeRefreshError(null);
    }

    // Honor refresh-token rotation + persist the idle window from the response.
    storeTokens(data);
    await refreshCloudfrontCookies();
    return data.access;
  })();

  // Clear the in-flight marker once settled (success or failure).
  refreshPromise.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const tokenRefreshCreate = async (token) => {
  try {
    const response = await axios.post(`${baseUrl}/api/token/refresh/`, {
      refresh: token
    });
    return response.data;
  } catch (err) {
    console.error("Error refreshing token:", err);
    return { error: err };
  }
};

const shouldRefreshCloudfrontCookies = () => {
  const expiry = localStorage.getItem('cloudfront_cookie_expiry');

  if (!expiry) return true;

  const expiryTime = Number(expiry);
  if (Number.isNaN(expiryTime)) return true;
  const refreshBuffer = 10 * 60 * 1000;

  return Date.now() >= (expiryTime - refreshBuffer);
};


let refreshCookiesPromise = null;

export const refreshCloudfrontCookies = async () => {
  if (refreshCookiesPromise) return refreshCookiesPromise;

  refreshCookiesPromise = (async () => {
    try {
      const tokensJson = localStorage.getItem('authTokens');
      const tokens = JSON.parse(tokensJson);

      const access = tokens?.access;

      if (access) {
        await axios.post(
          `${baseUrl}/api/refresh-cookies/`,
          {},
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${access}`,
            }
          }
        );

        const expiresIn = 86400;
        const expiryTime = Date.now() + (expiresIn * 1000);

        localStorage.setItem(
          'cloudfront_cookie_expiry',
          expiryTime.toString()
        );
      }

      return true;

    } catch (err) {
      console.error('Failed to refresh CloudFront cookies', err);
      return false;
    } finally {
      refreshCookiesPromise = null;
    }
  })();

  return refreshCookiesPromise;
};

export default instance;